// estate_prescribed.js — the estate view on a PRESCRIBED-item basis (2026-10).
//
// The generic basis (estate_model.js) lumps every brand and generic of a
// medicine onto one key and measures demand by what was DISPENSED. That hides
// the question a pharmacy actually has to answer: can my shelves fill what
// patients are being PRESCRIBED?
//
//   • Demand = what each branch had prescribed (prescribed side of the
//     paired usage report), matched to dm+d.
//   • A prescription by BRAND (e.g. "Adizem-XL 120mg capsules") can only be
//     filled by that brand - from any supplier, parallel imports included.
//   • A prescription by GENERIC name (e.g. "Gabapentin 300mg capsules") can be
//     filled by any product of that generic.
//
// Each branch's shelf stock is shared out between its own prescriptions -
// brand prescriptions first (only their brand will do), then generic ones
// (any brand of the generic will do) - and whatever is left is spare. Spare
// brand stock can still cover another branch's GENERIC prescription for the
// same medicine; that is carried as `altKey` and used by matchEstate as a
// second pass.
//
// Output is in exactly the shape estate_model.buildRows already reads
// (inventory documents + usage lookup/meta/monthly), so the balance maths,
// the worst-month test, the matching and every panel stay the one
// implementation. Pure: no React, no fetching.

const FORM_SYN = [
  [/\btabs?\b/g, "tablet"], [/\btablets\b/g, "tablet"],
  [/\bcaps?\b/g, "capsule"], [/\bcapsules\b/g, "capsule"],
  [/\bm\/r\b|\bmr\b/g, "modified release"], [/\bgastro resistant\b/g, "gastro-resistant"],
];
const PACK_TAIL = /\s+(?:pack of\s+)?\d+(?:\.\d+)?\s*(?:x\s*)?(?:tablet|capsule|doses?|ml|litres?|l|g|grams?|sachets?|patch(?:es)?|ampoules?|vials?|pessar(?:y|ies)|suppositor(?:y|ies)|pens?|devices?|inhalers?|units?|pieces?|packs?|pre-filled syringes?|lozenges?|strips?)?\s*$/;

// Product name -> comparison key. Hyphens between letters, bracketed text
// (supplier, pack) and a trailing pack count are dropped, and the common
// abbreviations unified, so dm+d and shelf names land on the same key.
export function nameKey(raw) {
  let s = String(raw || "").toLowerCase()
    .replace(/\(\s*\d+(?:\.\d+)?\s*(?:ml|g|tablet|tablets|capsule|capsules)?\s*\)/g, " ")
    .replace(/([a-z])-(?=[a-z])/g, "$1 ")
    .replace(/[,;]+/g, " ")
    .replace(/\s+/g, " ").trim();
  for (const [re, to] of FORM_SYN) s = s.replace(re, to);
  let prev;
  do { prev = s; s = s.replace(PACK_TAIL, "").trim(); } while (s !== prev && /\d/.test(s));
  return s.replace(/\s+/g, " ").trim();
}

// Readable label: the name without its bracketed text or pack tail.
export function nameLabel(raw) {
  let s = String(raw || "").replace(/\(.*?\)/g, " ").replace(/\s+/g, " ").trim();
  let prev;
  do { prev = s; s = s.replace(new RegExp(PACK_TAIL.source, "i"), "").trim(); } while (s !== prev && /\d/.test(s));
  return s;
}

// What a matched prescription asks for: a brand, or any product of a generic.
export function prescribedKey(match) {
  if (!match) return null;
  const amp = match.actualKey || nameKey(match.amppNm || (match.productType === "actual" ? match.productName : ""));
  const vmp = match.genericKey || nameKey(match.vmppNm || (match.productType === "virtual" ? match.productName : ""));
  const generic = match.productType === "virtual" || !amp || (vmp && amp === vmp);
  if (generic && vmp) return { key: `g:${vmp}`, kind: "generic", label: nameLabel(match.vmppNm || match.productName), generic: vmp };
  if (amp) return { key: `b:${amp}`, kind: "brand", label: nameLabel(match.amppNm || match.productName), generic: vmp || null };
  return null;
}

// What a shelf document IS: its brand key and its generic key.
export function stockKeys(item, ident) {
  const amp = ident?.actualKey || nameKey(ident?.amppNm || item.amppNm);
  const vmp = ident?.genericKey || nameKey(ident?.vmppNm || item.vmppNm);
  return { brand: amp || null, generic: vmp || null,
    brandLabel: nameLabel(item.amppNm || ident?.amppNm || item.vmppNm || ""),
    genericLabel: nameLabel(item.vmppNm || ident?.vmppNm || item.amppNm || "") };
}

function packetsOf(item) {
  const n = parseInt(item?.numberPackets, 10);
  if (Number.isFinite(n) && n !== 0) return n;
  const b = parseInt(item?.balance, 10);
  return Number.isFinite(b) ? b : 0;
}

/**
 * Freshness and windows from the prescribed side's own month coverage, in the
 * shapes estate_model.chooseUsageWindow / assessBranchesByWindow take.
 */
export function prescribedCoverage(demand) {
  const coverageByPharmacy = {};
  for (const c of demand?.coverage || []) {
    (coverageByPharmacy[c.pharmacyId] = coverageByPharmacy[c.pharmacyId] || {})[c.month] =
      { items: 1, partial: !c.complete, why: c.why };
  }
  const hasAnyUsage = new Set();
  for (const it of demand?.items || []) if ((it.monthly || []).some((m) => (m.qty || 0) > 0)) hasAnyUsage.add(it.pharmacyId);
  return { monthsAsc: demand?.monthsAsc || [], coverageByPharmacy, hasAnyUsage };
}

/**
 * Builds the prescribed-basis inputs for estate_model.buildRows.
 *
 * inventoryData   shelf documents (/ggetInventory)
 * identity        gtin -> { amppNm, vmppNm, supplier } (/stockDmdIdentity)
 * demand          /usagePrescribedDemand result
 * windows         pharmacyId -> { months: [ym…] } (chooseUsageWindow per branch)
 * monthsAhead     cover period: decides how much stock each prescription keeps
 */
export function buildPrescribedInputs({ inventoryData = [], identity = {}, demand = null, windows = {}, monthsAhead = 3, n = 3 }) {
  const items = demand?.items || [];
  const monthsAsc = demand?.monthsAsc || [];

  // ---- demand per branch per prescribed product --------------------------
  const keyInfo = new Map();          // key -> { label, kind, generic, deviceMode }
  const demandBy = new Map();         // `${pid}|${key}` -> { pid, key, monthly: Map(month->qty), names:Set }
  let unmatched = 0, unmatchedUnits = 0;
  for (const it of items) {
    const pk = prescribedKey(it.match);
    if (!pk) { unmatched++; unmatchedUnits += (it.monthly || []).reduce((t, m) => t + (m.qty || 0), 0); continue; }
    const info = keyInfo.get(pk.key) || { ...pk, deviceMode: false, packSize: 0 };
    if (it.packDivisor === 1 && it.dosesPerDevice) info.deviceMode = true;
    // dm+d pack size: the pack a short branch is reasoned in until it holds stock.
    if (!info.packSize && Number(it.match?.qtval) > 0) info.packSize = Number(it.match.qtval);
    keyInfo.set(pk.key, info);
    const id = `${it.pharmacyId}|${pk.key}`;
    const d = demandBy.get(id) || { pid: it.pharmacyId, key: pk.key, monthly: new Map(), names: new Set() };
    for (const m of it.monthly || []) if (m.qty != null) d.monthly.set(m.month, (d.monthly.get(m.month) || 0) + m.qty);
    d.names.add(it.name);
    demandBy.set(id, d);
  }

  const usageLookup = {}, usageMeta = {}, usageMonthly = {};
  const needUnits = new Map();        // `${pid}|${key}` -> units for monthsAhead
  for (const d of demandBy.values()) {
    const win = windows[d.pid]?.months?.length ? windows[d.pid].months : monthsAsc.slice(-n);
    const monthly = win.map((month) => ({ month, qty: d.monthly.get(month) || 0 }));
    const avg = win.length ? monthly.reduce((t, m) => t + m.qty, 0) / win.length : 0;
    const k = `${d.pid}_${d.key}`;
    const info = keyInfo.get(d.key);
    usageLookup[k] = avg;
    usageMonthly[k] = monthly;
    usageMeta[k] = { pharmacyId: d.pid, drugName: info.label, cleanName: d.key, snomed: null,
      packSize: info.deviceMode ? 1 : (info.packSize || 0),
      prescribedAs: [...d.names], basis: info.kind,
      altKey: null };
    needUnits.set(`${d.pid}|${d.key}`, avg * monthsAhead);
  }
  const demanded = new Set(keyInfo.keys());
  const eligibility = new Map();
  for (const item of items) {
    const pk = prescribedKey(item.match);
    if (!pk) continue;
    const e = eligibility.get(pk.key) || { ampps: new Set(), vpps: new Set(), restricted: true };
    for (const id of item.match?.eligibleAmppids || []) e.ampps.add(String(id));
    for (const id of item.match?.eligibleVppids || []) e.vpps.add(String(id));
    if (!item.match?.restrictStockToEligibleAmpp) e.restricted = false;
    eligibility.set(pk.key, e);
  }
  const canSupply = (doc, key) => {
    const e = eligibility.get(key);
    const ampp = String(doc.ident?.amppid || doc.item.amppid || "");
    const vpp = String(doc.ident?.vppid || doc.item.vppid || "");
    if (e?.ampps.has(ampp)) return true;
    if (e?.restricted) return false;
    if (key.startsWith("g:")) return e?.vpps.has(vpp) || doc.ks.generic === key.slice(2);
    return doc.ks.brand === key.slice(2);
  };
  const byAmpp = new Map(), byVpp = new Map(), byBrand = new Map(), byGeneric = new Map();
  const addEligibility = (map, id, key) => {
    if (!map.has(id)) map.set(id, new Set());
    map.get(id).add(key);
  };
  for (const [key, e] of eligibility) {
    for (const id of e.ampps) addEligibility(byAmpp, id, key);
    if (!e.restricted) {
      if (key.startsWith("g:")) {
        for (const id of e.vpps) addEligibility(byVpp, id, key);
        addEligibility(byGeneric, key.slice(2), key);
      } else addEligibility(byBrand, key.slice(2), key);
    }
  }
  const demandKeysForStock = doc => [...new Set([
    ...(byAmpp.get(String(doc.ident?.amppid || doc.item.amppid || "")) || []),
    ...(byVpp.get(String(doc.ident?.vppid || doc.item.vppid || "")) || []),
    ...(byBrand.get(doc.ks.brand) || []), ...(byGeneric.get(doc.ks.generic) || []),
  ])];
  const stockUsageAllocations = [];
  const stockDemandCache = new Map();
  // dm+d pack identifiers take precedence over display-name fallbacks.
  const genericByVpp = new Map(), genericByAmpp = new Map(), brandByAmpp = new Map();
  for (const item of items) {
    const pk = prescribedKey(item.match);
    if (!pk) continue;
    for (const id of item.match?.eligibleVppids || []) {
      if (pk.kind === "generic" && !item.match?.restrictStockToEligibleAmpp) genericByVpp.set(String(id), pk.generic);
    }
    if (pk.kind === "generic") for (const id of item.match?.eligibleAmppids || []) genericByAmpp.set(String(id), pk.generic);
    if (pk.kind === "brand") for (const id of item.match?.eligibleAmppids || []) brandByAmpp.set(String(id), pk.key.slice(2));
  }
  const identityByGtin = new Map(Object.entries(identity).map(([gtin, value]) => [String(gtin).replace(/^0+/, ""), value]));
  const unresolvedStock = [];

  // ---- share each branch's shelf stock between its prescriptions ---------
  const shelf = inventoryData.filter((it) => String(it.status || "").toLowerCase().includes("shelf"));
  const byBranch = new Map();
  for (const it of shelf) {
    const packs = packetsOf(it);
    if (!(packs > 0)) continue;
    const ident = identity[String(it.gtin)] || identityByGtin.get(String(it.gtin).replace(/^0+/, ""));
    const ks = stockKeys(it, ident);
    ks.generic = genericByAmpp.get(String(ident?.amppid || it.amppid)) || genericByVpp.get(String(ident?.vppid || it.vppid)) || ks.generic;
    ks.brand = brandByAmpp.get(String(ident?.amppid || it.amppid)) || ks.brand;
    const qty = Number(it.qtval) > 0 ? Number(it.qtval) : Number(ident?.qtval);
    if (ident?.ambiguous || (!ks.brand && !ks.generic) || !(qty > 0)) {
      unresolvedStock.push({ gtin: it.gtin || "", pharmacyId: it.pharmacyId, packs,
        reason: ident?.ambiguous ? "Ambiguous GTIN" : !ks.brand && !ks.generic ? "Product identity missing" : "Pack quantity missing" });
      continue;
    }
    const list = byBranch.get(it.pharmacyId) || [];
    list.push({ item: { ...it, qtval: qty }, ident, ks, left: packs });
    byBranch.set(it.pharmacyId, list);
  }

  const out = [];
  const emit = (doc, packs, key, label, altKey, allocatedUnits = 0) => {
    if (packs <= 0) return;
    const info = keyInfo.get(key);
    out.push({
      ...doc.item,
      numberPackets: packs, balance: packs,
      // Inhalers: prescriptions count devices, so a device is the unit here.
      qtval: info?.deviceMode ? 1 : (parseFloat(doc.item.qtval) || 1),
      estateKey: key, estateLabel: label, altKey: altKey || null,
      hasPrescribedUsage: doc.hasPrescribedUsage,
      stockDemandKeys: doc.stockDemandKeys,
      prescribedUnitsCovered: allocatedUnits,
      brandName: doc.ks.brandLabel, genericName: doc.ks.genericLabel, supplier: doc.ident?.supplier || doc.item.supplier || "",
    });
  };
  const unitsPerPack = (doc, key) => (keyInfo.get(key)?.deviceMode ? 1 : (parseFloat(doc.item.qtval) || 1));
  const soonest = (a, b) => String(a.item.expiryDate || "9999").localeCompare(String(b.item.expiryDate || "9999"));

  for (const [pid, docs] of byBranch) {
    docs.sort(soonest);
    const take = (key, eligible) => {
      const remainingKey = `${pid}|${key}`;
      let need = needUnits.get(remainingKey) || 0;
      if (need <= 0) return;
      const info = keyInfo.get(key);
      for (const doc of docsByDemand.get(key) || []) {
        if (need <= 0) break;
        if (doc.left <= 0 || !eligible(doc)) continue;
        const per = unitsPerPack(doc, key);
        const packs = Math.min(doc.left, Math.ceil(need / per));
        const coveredUnits = Math.min(need, packs * per);
        doc.left -= packs; need -= packs * per;
        stockUsageAllocations.push({ pharmacyId: pid, stockId: doc.item._id || null,
          gtin: doc.item.gtin, demandKey: key, prescribedName: info.label, packs, coveredUnits });
        emit(doc, packs, key, info.label, null, coveredUnits);
      }
      needUnits.set(remainingKey, Math.max(0, need));
    };
    const keysHere = [...demandBy.values()].filter((d) => d.pid === pid).map((d) => d.key);
    const activeKeysHere = keysHere.filter(key => usageLookup[`${pid}_${key}`] > 0);
    const activeKeySet = new Set(activeKeysHere);
    const activeBrandKeys = new Set(activeKeysHere.filter(key => key.startsWith("b:")));
    const docsByDemand = new Map();
    for (const doc of docs) {
      const identityKey = JSON.stringify([doc.ident?.amppid || doc.item.amppid, doc.ident?.vppid || doc.item.vppid, doc.ks.brand, doc.ks.generic]);
      if (!stockDemandCache.has(identityKey)) stockDemandCache.set(identityKey, demandKeysForStock(doc));
      doc.stockDemandKeys = stockDemandCache.get(identityKey);
      doc.hasPrescribedUsage = doc.stockDemandKeys.some(key => activeKeySet.has(key));
      doc.hasBrandDemandHere = doc.stockDemandKeys.some(key => activeBrandKeys.has(key));
      for (const key of doc.stockDemandKeys) {
        if (!docsByDemand.has(key)) docsByDemand.set(key, []);
        docsByDemand.get(key).push(doc);
      }
    }
    // 1. Brand prescriptions: only that brand will do.
    for (const key of keysHere.filter((k) => k.startsWith("b:"))) take(key, (doc) => canSupply(doc, key));
    // 2. Generic prescriptions: any brand of the generic - stock whose brand
    //    nobody prescribes is used first, so brands stay for brand scripts.
    for (const key of keysHere.filter((k) => k.startsWith("g:"))) {
      take(key, (doc) => canSupply(doc, key) && !doc.hasBrandDemandHere);
      take(key, (doc) => canSupply(doc, key));
    }
    // 3. Whatever is left is spare. It sits under the brand when someone in
    //    the estate prescribes that brand (and can also cover a generic script
    //    for the same medicine - altKey), otherwise under the generic.
    for (const doc of docs) {
      if (doc.left <= 0) continue;
      const bk = doc.ks.brand ? `b:${doc.ks.brand}` : null;
      const gk = doc.ks.generic ? `g:${doc.ks.generic}` : null;
      if (bk && demanded.has(bk)) emit(doc, doc.left, bk, keyInfo.get(bk).label, gk && demanded.has(gk) ? gk : null);
      else if (gk && demanded.has(gk)) emit(doc, doc.left, gk, keyInfo.get(gk).label, null);
      else emit(doc, doc.left, `s:${doc.ks.generic || doc.ks.brand}`, doc.ks.genericLabel || doc.ks.brandLabel, null);
      doc.left = 0;
    }
  }

  // Usage rows for a brand also accept the generic's spare stock? No - a brand
  // script cannot be filled by another brand. But a GENERIC row's demand can
  // be met by spare brand stock elsewhere: that link is the brand rows' altKey,
  // set above on the shelf documents. Rows with usage but no stock carry none.

  // Label -> key, so pending transfers (recorded by drug name) net off the
  // right rows.
  const labelToKey = new Map();
  for (const [key, info] of keyInfo) labelToKey.set(nameKey(info.label), key);
  for (const it of out) if (!labelToKey.has(nameKey(it.estateLabel))) labelToKey.set(nameKey(it.estateLabel), it.estateKey);
  const keyOfDrug = (drug) => {
    const k = nameKey(drug);
    if (labelToKey.has(k)) return labelToKey.get(k);
    if (demanded.has(`b:${k}`)) return `b:${k}`;
    if (demanded.has(`g:${k}`)) return `g:${k}`;
    return `s:${k}`;
  };

  return {
    inventoryData: out,
    usage: { lookup: usageLookup, meta: usageMeta, monthly: usageMonthly },
    keyOfDrug,
    stats: { stockUsageAllocations, unresolvedStock, unresolvedStockCount: unresolvedStock.length, prescribedProducts: keyInfo.size, demandLines: demandBy.size, unmatched, unmatchedUnits,
      brandProducts: [...keyInfo.values()].filter((i) => i.kind === "brand").length },
  };
}
